import { useEffect, useRef, useState } from "react";
import { previewKpiCard } from "../../services/kpiApi";
import {
    BLOCK_LABELS, GRADE_CODES, TIME_TYPE_LABELS, deltaClass, emptyLine, emptyTask, hoursLabel, money, moveItem,
    percent, plain, variableWeight,
} from "../../utils/kpiFormat";

/**
 * Карта КПЭ сотрудника — как в Google-таблице, но расчёт делает платформа. [Claude] 2026-11-02, Чат 6, №10.
 * Серые поля считаются сами; пересчёт идёт на сервере при каждом изменении (без сохранения).
 */

const cell = "w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-sm outline-none focus:border-amber-400 disabled:bg-slate-50";
const area = `${cell} min-h-[56px] resize-y`;
const calcCell = "px-2 py-1 text-right text-slate-600 bg-slate-50 whitespace-nowrap";
const btn = "rounded-lg px-2.5 py-1 text-xs font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : v);

function NumInput({ value, onChange, disabled, className = "" }) {
    return (
        <input className={`${cell} text-right ${className}`} inputMode="decimal" disabled={disabled}
            value={value ?? ""} onChange={(e) => onChange(numOrNull(e.target.value.replace(",", ".")))} />
    );
}

function Field({ label, children, wide }) {
    return (
        <label className={`text-xs text-slate-500 ${wide ? "sm:col-span-2" : ""}`}>
            {label}
            <div className="mt-1">{children}</div>
        </label>
    );
}

function Calc({ label, value, cls = "" }) {
    return (
        <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-xs text-slate-500">{label}</p>
            <p className={`font-semibold ${cls || "text-slate-800"}`}>{value}</p>
        </div>
    );
}

export default function KpiCardEditor({ monthId, card, readOnly, onSave, onDelete, onClose, saving }) {
    const [draft, setDraft] = useState(card);
    const [calc, setCalc] = useState(card);
    const [dirty, setDirty] = useState(false);
    const timer = useRef(null);

    useEffect(() => { setDraft(card); setCalc(card); setDirty(false); }, [card]);

    useEffect(() => {
        if (!dirty) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            try {
                setCalc(await previewKpiCard(monthId, draft));
            } catch {
                /* пересчёт покажется после сохранения */
            }
        }, 400);
        return () => clearTimeout(timer.current);
    }, [draft, dirty, monthId]);

    const set = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
    const setLine = (i, patch) => set({ lines: draft.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
    const setTask = (i, patch) => set({ tasks: draft.tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
    const lineCalc = (i) => calc?.lines?.[i] || {};
    const taskCalc = (i) => calc?.tasks?.[i] || {};
    const weight = variableWeight(draft.lines);
    const diff = card.sheet_diff && Object.keys(card.sheet_diff).length ? card.sheet_diff : null;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <input className={`${cell} max-w-md text-base font-semibold`} value={draft.title || ""} disabled={readOnly}
                    onChange={(e) => set({ title: e.target.value })} aria-label="Название карты" />
                <div className="flex-1" />
                {dirty && <span className="text-xs text-amber-700">Есть несохранённые изменения</span>}
                {!readOnly && (
                    <button type="button" className={`${btn} bg-[#e0bb48] text-black`} disabled={!dirty || saving}
                        onClick={() => onSave(draft)}>
                        {saving ? "Сохраняю…" : "Сохранить"}
                    </button>
                )}
                {!readOnly && dirty && (
                    <button type="button" className={btnGhost} onClick={() => { setDraft(card); setCalc(card); setDirty(false); }}>
                        Отменить
                    </button>
                )}
                {!readOnly && onDelete && <button type="button" className={`${btnGhost} text-red-600`} onClick={onDelete}>Удалить карту</button>}
                {onClose && <button type="button" className={btnGhost} onClick={onClose}>Закрыть</button>}
            </div>

            {diff && (
                <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    Расчёт платформы отличается от Google-таблицы при переносе:{" "}
                    {Object.entries(diff).map(([k, v]) => `${k}: таблица ${plain(v.sheet)} / платформа ${plain(v.platform)}`).join("; ")}
                </p>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
                <Field label="Сотрудник (ФИО)" wide>
                    <input className={cell} value={draft.employee_name || ""} disabled={readOnly} onChange={(e) => set({ employee_name: e.target.value })} />
                </Field>
                <Field label="Должность" wide>
                    <input className={cell} value={draft.position || ""} disabled={readOnly} onChange={(e) => set({ position: e.target.value })} />
                </Field>
                <Field label="Проф. грейд">
                    <select className={cell} value={draft.grade || ""} disabled={readOnly} onChange={(e) => set({ grade: e.target.value || null })}>
                        <option value="">—</option>
                        {GRADE_CODES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Оклад, ₽"><NumInput value={draft.salary} disabled={readOnly} onChange={(v) => set({ salary: v })} /></Field>
                <Field label="Постоянная часть, %"><NumInput value={draft.fixed_share_pct} disabled={readOnly} onChange={(v) => set({ fixed_share_pct: v })} /></Field>
                <Field label="Норма часов"><NumInput value={draft.norm_hours} disabled={readOnly} onChange={(v) => set({ norm_hours: v })} /></Field>
                <Field label="Факт часов"><NumInput value={draft.fact_hours} disabled={readOnly} onChange={(v) => set({ fact_hours: v })} /></Field>
                <Field label="Доплата вручную, ₽"><NumInput value={draft.extra_manual} disabled={readOnly} onChange={(v) => set({ extra_manual: v })} /></Field>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
                <Calc label="Постоянная часть" value={money(calc.fixed_part)} />
                <Calc label="Коэф. раб. времени" value={percent(calc.work_coef)} />
                <Calc label="Пост. часть с учётом отр. вр." value={money(calc.fixed_adjusted)} />
                <Calc label="Баллы план / факт" value={`${plain(calc.points_plan_total, 0)} / ${plain(calc.points_fact_total, 0)}`} />
                <Calc label="Итого по КПЭ" value={`${money(calc.kpi_calc)} из ${money(calc.kpi_plan)}`} />
                <Calc label="Дельта" value={money(calc.kpi_delta)} cls={deltaClass(calc.kpi_delta)} />
                <Calc label="Доп. задачи" value={money(calc.extra_tasks_paid)} />
                <Calc label="ИТОГО к оплате" value={money(calc.total_pay)} cls="text-slate-900 text-lg" />
            </div>

            <section>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-slate-800">Показатели</h3>
                    <span className={`text-xs ${weight === 100 ? "text-slate-500" : "text-amber-700"}`}>
                        вес переменной части: {plain(weight, 1)} %{weight !== 100 ? " (обычно 100 %)" : ""}
                    </span>
                    <div className="flex-1" />
                    {!readOnly && Object.entries(BLOCK_LABELS).map(([b, label]) => (
                        <button key={b} type="button" className={btnGhost} onClick={() => set({ lines: [...draft.lines, emptyLine(b)] })}>
                            + {label.toLowerCase()}
                        </button>
                    ))}
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-[1500px] w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                            <tr className="text-left">
                                <th className="px-2 py-2 w-36">Блок</th>
                                <th className="px-2 py-2 w-16">Вес %</th>
                                <th className="px-2 py-2 w-16">Баллы план</th>
                                <th className="px-2 py-2 w-16">Баллы факт</th>
                                <th className="px-2 py-2 w-14">Грейд задачи</th>
                                <th className="px-2 py-2 min-w-[260px]">Показатель / задача</th>
                                <th className="px-2 py-2 min-w-[200px]">Результат</th>
                                <th className="px-2 py-2 w-20">Метрика план</th>
                                <th className="px-2 py-2 w-20">Метрика факт</th>
                                <th className="px-2 py-2 w-16">Баллы замена</th>
                                <th className="px-2 py-2 w-16" title="Подтверждено перевыполнение (+20 % к лимиту при коэф. < 98 %)">Перевып.</th>
                                <th className="px-2 py-2 text-right">% КПЭ</th>
                                <th className="px-2 py-2 text-right">% вып.</th>
                                <th className="px-2 py-2 text-right">% к оплате</th>
                                <th className="px-2 py-2 text-right">План ₽</th>
                                <th className="px-2 py-2 text-right">Расчёт ₽</th>
                                <th className="px-2 py-2 text-right">Доплата</th>
                                {!readOnly && <th className="px-2 py-2 w-20" />}
                            </tr>
                        </thead>
                        <tbody>
                            {draft.lines.map((l, i) => {
                                const c = lineCalc(i);
                                const isVar = l.block === "variable";
                                const isBonus = l.block === "bonus";
                                return (
                                    <tr key={i} className="border-t border-slate-100 align-top">
                                        <td className="px-2 py-1">
                                            <select className={cell} value={l.block} disabled={readOnly} onChange={(e) => setLine(i, { block: e.target.value })}>
                                                {Object.entries(BLOCK_LABELS).map(([b, label]) => <option key={b} value={b}>{label}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-1 py-1">{isVar && <NumInput value={l.weight} disabled={readOnly} onChange={(v) => setLine(i, { weight: v })} />}</td>
                                        <td className="px-1 py-1">
                                            {l.block === "fixed" ? <NumInput value={l.points_plan} disabled={readOnly} onChange={(v) => setLine(i, { points_plan: v })} />
                                                : <span className="block px-1.5 py-1 text-right text-slate-500">{plain(c.points_plan, 1)}</span>}
                                        </td>
                                        <td className="px-1 py-1">
                                            {l.block === "fixed" ? <NumInput value={l.points_fact} disabled={readOnly} onChange={(v) => setLine(i, { points_fact: v })} />
                                                : <span className="block px-1.5 py-1 text-right text-slate-500">{plain(c.points_fact, 1)}</span>}
                                        </td>
                                        <td className="px-1 py-1">
                                            {!isBonus && (
                                                <select className={cell} value={l.task_grade || ""} disabled={readOnly} onChange={(e) => setLine(i, { task_grade: e.target.value || null })}>
                                                    <option value="">—</option>
                                                    {GRADE_CODES.map((g) => <option key={g} value={g}>{g}</option>)}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-1 py-1"><textarea className={area} value={l.indicator || ""} disabled={readOnly} onChange={(e) => setLine(i, { indicator: e.target.value })} /></td>
                                        <td className="px-1 py-1"><textarea className={area} value={l.result || ""} disabled={readOnly} onChange={(e) => setLine(i, { result: e.target.value })} /></td>
                                        {isBonus ? (
                                            <td className="px-1 py-1" colSpan={4}>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <label className="text-xs text-slate-500">План бонуса, ₽
                                                        <NumInput value={l.plan_amount} disabled={readOnly} onChange={(v) => setLine(i, { plan_amount: v })} />
                                                    </label>
                                                    <label className="text-xs text-slate-500">Начислено, ₽
                                                        <NumInput value={l.amount} disabled={readOnly} onChange={(v) => setLine(i, { amount: v })} />
                                                    </label>
                                                </div>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="px-1 py-1"><NumInput value={l.metric_plan} disabled={readOnly} onChange={(v) => setLine(i, { metric_plan: v })} /></td>
                                                <td className="px-1 py-1"><NumInput value={l.metric_fact} disabled={readOnly} onChange={(v) => setLine(i, { metric_fact: v })} /></td>
                                                <td className="px-1 py-1">{isVar && <NumInput value={l.points_replace} disabled={readOnly} onChange={(v) => setLine(i, { points_replace: v })} />}</td>
                                                <td className="px-1 py-1 text-center">
                                                    {isVar && <input type="checkbox" checked={Boolean(l.overperf_confirmed)} disabled={readOnly}
                                                        onChange={(e) => setLine(i, { overperf_confirmed: e.target.checked })} aria-label="Перевыполнение подтверждено" />}
                                                </td>
                                            </>
                                        )}
                                        <td className={calcCell}>{percent(c.kpi_pct)}</td>
                                        <td className={calcCell}>{percent(c.done_pct)}</td>
                                        <td className={calcCell}>{percent(c.paid_pct)}</td>
                                        <td className={calcCell}>{money(c.plan_cost)}</td>
                                        <td className={calcCell}>{money(c.calc)}</td>
                                        <td className={`${calcCell} ${deltaClass(c.delta)}`}>{money(c.delta)}</td>
                                        {!readOnly && (
                                            <td className="px-1 py-1 whitespace-nowrap">
                                                <button type="button" className="px-1 text-slate-400 hover:text-slate-700" onClick={() => set({ lines: moveItem(draft.lines, i, -1) })} aria-label="Выше">↑</button>
                                                <button type="button" className="px-1 text-slate-400 hover:text-slate-700" onClick={() => set({ lines: moveItem(draft.lines, i, 1) })} aria-label="Ниже">↓</button>
                                                <button type="button" className="px-1 text-red-400 hover:text-red-600" onClick={() => set({ lines: draft.lines.filter((_, j) => j !== i) })} aria-label="Удалить строку">✕</button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            {!draft.lines.length && <tr><td colSpan={18} className="px-3 py-3 text-slate-500">Показателей нет</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>

            <section>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-slate-800">Дополнительные задачи</h3>
                    <span className="text-xs text-slate-500">
                        стоимость = часы × ставка грейда; к оплате не больше {money((calc.fixed_part || 0) * 0.2)} (20 % постоянной части)
                    </span>
                    <div className="flex-1" />
                    {!readOnly && <button type="button" className={btnGhost} onClick={() => set({ tasks: [...draft.tasks, emptyTask()] })}>+ задача</button>}
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-[900px] w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                            <tr className="text-left">
                                <th className="px-2 py-2 min-w-[260px]">Задача</th>
                                <th className="px-2 py-2 w-24">Время (ч:мм)</th>
                                <th className="px-2 py-2 w-20">Грейд</th>
                                <th className="px-2 py-2 w-32">Когда</th>
                                <th className="px-2 py-2 min-w-[160px]">Подтверждение / комментарий</th>
                                <th className="px-2 py-2 text-right">Стоимость</th>
                                <th className="px-2 py-2">Категория</th>
                                {!readOnly && <th className="px-2 py-2 w-10" />}
                            </tr>
                        </thead>
                        <tbody>
                            {draft.tasks.map((t, i) => {
                                const c = taskCalc(i);
                                return (
                                    <tr key={i} className="border-t border-slate-100 align-top">
                                        <td className="px-1 py-1"><textarea className={area} value={t.title || ""} disabled={readOnly} onChange={(e) => setTask(i, { title: e.target.value })} /></td>
                                        <td className="px-1 py-1">
                                            <input className={cell} placeholder="2:30" disabled={readOnly}
                                                value={typeof t.hours === "number" ? hoursLabel(t.hours) : (t.hours ?? "")}
                                                onChange={(e) => setTask(i, { hours: e.target.value })} />
                                        </td>
                                        <td className="px-1 py-1">
                                            <select className={cell} value={t.grade || ""} disabled={readOnly} onChange={(e) => setTask(i, { grade: e.target.value || null })}>
                                                <option value="">—</option>
                                                {GRADE_CODES.map((g) => <option key={g} value={g}>{g}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-1 py-1">
                                            <select className={cell} value={t.time_type || "work"} disabled={readOnly} onChange={(e) => setTask(i, { time_type: e.target.value })}>
                                                {Object.entries(TIME_TYPE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-1 py-1"><input className={cell} value={t.comment || ""} disabled={readOnly} onChange={(e) => setTask(i, { comment: e.target.value })} /></td>
                                        <td className={calcCell}>{money(c.cost)}</td>
                                        <td className="px-2 py-1 text-xs text-slate-500">{c.grade_category || ""}</td>
                                        {!readOnly && (
                                            <td className="px-1 py-1">
                                                <button type="button" className="px-1 text-red-400 hover:text-red-600" onClick={() => set({ tasks: draft.tasks.filter((_, j) => j !== i) })} aria-label="Удалить задачу">✕</button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            {!draft.tasks.length && <tr><td colSpan={8} className="px-3 py-3 text-slate-500">Доп. задач нет</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>

            <Field label="Комментарий к карте">
                <textarea className={area} value={draft.comment || ""} disabled={readOnly} onChange={(e) => set({ comment: e.target.value })} />
            </Field>
        </div>
    );
}
